import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import { sourceToAbilityMap } from 'garaje/abilities/entry-approval';
import UnifiedScreenersModal from 'garaje/components/approvals/unified-screeners-modal';
import UiButton from 'garaje/components/ui-button';
import type ScreenerResult from 'garaje/models/screener-result';
import type { GuestWithVisitApproval } from 'garaje/models/visit-approval';
import type MetricsService from 'garaje/services/metrics';
import getModelClassName from 'garaje/utils/get-model-class-name';
import { getScreenerDisplayName } from 'garaje/utils/get-screener-display-name';

interface ScreenerResultDetailsSignature {
  Args: {
    result: ScreenerResult;
    model: GuestWithVisitApproval;
    context?: 'location' | 'property';
  };
  Element: HTMLUListElement;
}

export default class ScreenerResultDetails extends Component<ScreenerResultDetailsSignature> {
  @service declare abilities: AbilitiesService;
  @service declare metrics: MetricsService;

  @tracked showModal = false;

  @action
  toggleModal(): void {
    this.showModal = !this.showModal;

    if (this.showModal) {
      this.metrics.trackEvent('Unified Screener Modal Opened', {
        screenerSource: this.args.result.screenerSource,
        from: `Drilled in view: ${getModelClassName(this.args.model)}`,
      });
    }
  }

  get canView(): boolean {
    const {
      abilities,
      context,
      args: { model, result },
    } = this;

    const source = sourceToAbilityMap[result.screenerSource] || result.screenerSource;

    if (abilities.can(`review ${source}`, { context, model, visitApproval: model.visitApproval })) return true;
    if (abilities.can(`view ${source}`, { context, model, visitApproval: model.visitApproval })) return true;

    return false;
  }

  get context(): string {
    return this.args.context ?? 'location';
  }

  <template>
    {{#if this.canView}}
      <h4 class="text-sm capitalize mb-2">{{getScreenerDisplayName @result.screenerSource}}</h4>
      <p class="text-sm ml-2 mb-0">Matched at least one restricted party.</p>
      <ul ...attributes data-test-screener-result-details={{@result.screenerSource}}>
        <li>
          <span>Review Match Details</span>
          <UiButton
            class="btn btn--link btn--small !text-gem-50"
            {{on "click" this.toggleModal}}
            data-test-screener-results-detail-trigger
          >
            here
          </UiButton>
        </li>
      </ul>

      {{#if this.showModal}}
        <UnifiedScreenersModal @model={{@model}} @focusedResult={{@result}} @onClose={{this.toggleModal}} />
      {{/if}}
    {{/if}}
  </template>
}
